import React, { useState } from 'react';

import { Alert, Button, Checkbox, DatePicker, Form, Modal, Select, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { usePurchaseSubscriptionMutation } from 'features/adminOperations/api';
import { SubscriptionPlan } from 'features/adminOperations/types';
import { SelectUsers } from 'features/users';

interface PurchaseModalProps {
  plans: SubscriptionPlan[];
  onClose: () => void;
}

interface PurchaseForm {
  userId: number;
  planId: number;
  purchasedOn: Dayjs;
  paid: boolean;
}

export function PurchaseModal({ plans, onClose }: PurchaseModalProps) {
  const { t } = useTranslation();
  const [purchase, { isLoading }] = usePurchaseSubscriptionMutation();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [form] = Form.useForm<PurchaseForm>();
  const selectedPlan = Form.useWatch('planId', form);
  const plan = plans.find((item) => item.id === selectedPlan);

  const handlePurchase = async (values: PurchaseForm) => {
    try {
      await purchase({
        'idempotency_key': idempotencyKey,
        'user_id': values.userId,
        'plan_id': values.planId,
        'purchased_on': values.purchasedOn.format('YYYY-MM-DD'),
        'payment_confirmed': true,
      }).unwrap();
      message.success(t('admin-extensions.purchase-success'));
      onClose();
    } catch {
      message.error(t('admin-extensions.purchase-error'));
    }
  };

  return (
    <Modal open title={t('admin-extensions.issue-subscription')} onCancel={onClose} footer={null} destroyOnClose>
      <Alert type="info" showIcon message={t('admin-extensions.purchase-info')} style={{ marginBottom: 16 }} />
      <Form<PurchaseForm> form={form} layout="vertical" initialValues={{ purchasedOn: dayjs() }} onFinish={(values) => {
        void handlePurchase(values);
      }}
      >
        <SelectUsers oneSelectMode formProps={{ label: t('admin-extensions.client'), rules: [{ required: true }] }} />
        <Form.Item name="planId" label={t('admin-extensions.plan')} rules={[{ required: true }]}>
          <Select
            placeholder={t('admin-extensions.select-plan')}
            options={plans.map((item) => ({ value: item.id, label: `${item.name} — ${item.price} ₽` }))}
          />
        </Form.Item>
        {plan && <p>{t('admin-extensions.plan-info', { count: plan.lessons, price: plan.price })}</p>}
        <Form.Item name="purchasedOn" label={t('admin-extensions.purchase-date')} rules={[{ required: true }]}>
          <DatePicker disabledDate={(date) => date.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="paid" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error(t('admin-extensions.confirm-payment'))) }]}>
          <Checkbox>{t('admin-extensions.confirm-payment')}</Checkbox>
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>{t('admin-extensions.issue')}</Button>
      </Form>
    </Modal>
  );
}
